import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  deleteDoc,
  doc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { useBuyer } from "../contexts/BuyerContext";
import Header from "../components/Header";
import "./Orders.css";

const Orders = () => {
  const { type } = useParams();
  const location = useLocation();
  const serviceType = location.state?.serviceType || null;

  const { buyer } = useBuyer();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const orderTypeConfig = useMemo(
    () => ({
      requested: {
        status: "Requested",
        title: "Requested Orders",
        showDelete: true,
      },
      rejected: {
        status: "Rejected",
        title: "Rejected Orders",
        showDelete: false,
      },
      inProgress: {
        statuses: [
          "Accepted",
          "Assigned",
          "Assortment",
          "Assorted",
          "FinalCorrection",
          "RTD",
        ],
        title: "Orders in Progress",
        showDelete: false,
      },
      delivered: {
        statuses: ["delivered", "shipped", "out-for-delivery"],
        title: "Delivered Orders",
        showDelete: false,
      },
      cancelled: {
        status: "Cancelled",
        title: "Cancelled Orders",
        showDelete: false,
      },
    }),
    [],
  );

  const config = orderTypeConfig[type] || orderTypeConfig.requested;
  // Fetch orders based on type
  const fetchOrders = useCallback(async () => {
    const buyerId = buyer?.buyerId;

    if (!buyerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let fetchedOrders = [];

      if (config.statuses) {
        const ordersRef = collection(db, "orderList");
        for (const status of config.statuses) {
          const constraints = [
            where("buyerId", "==", buyerId),
            where("orderStatus", "==", status),
          ];
          if (serviceType) {
            constraints.push(where("serviceType", "==", serviceType));
          }
          const q = query(ordersRef, ...constraints);
          const snapshot = await getDocs(q);
          const ordersForStatus = snapshot.docs.map((docSnap) => ({
            firebaseDocId: docSnap.id, // This is the actual Firebase document ID
            ...docSnap.data(),
          }));

          fetchedOrders = [...fetchedOrders, ...ordersForStatus];
        }
      } else {
        const constraints = [
          where("buyerId", "==", buyerId),
          where("orderStatus", "==", config.status),
        ];
        if (serviceType) {
          constraints.push(where("serviceType", "==", serviceType));
        }
        const q = query(collection(db, "orderList"), ...constraints);
        const snapshot = await getDocs(q);
        fetchedOrders = snapshot.docs.map((docSnap) => ({
          firebaseDocId: docSnap.id, // This is the actual Firebase document ID
          ...docSnap.data(),
        }));
      }
      setOrders(fetchedOrders);
      console.log("Fetched orders with Firebase document IDs:", fetchedOrders);
    } catch (err) {
      console.error(`Error fetching ${config.title}:`, err);
      setError(`Failed to load ${config.title}.`);
    } finally {
      setLoading(false);
    }
  }, [buyer, config, serviceType]);

  useEffect(() => {
    fetchOrders();
  }, [buyer?.buyerId, type, config, fetchOrders]);

  const calculateProductWeights = (product) => {
    const num = (v) => parseFloat(v) || 0;
    // ✅ Total wastage %
    const wastage =
      parseFloat(product?.buyerDeskCalculation || 0) +
      parseFloat(product?.wastage || 0) +
      parseFloat(product?.wastageOperator || 0) +
      parseFloat(product?.wastageJmi || 0) +
      parseFloat(product?.specificationMC || 0) +
      (product?.specification && product.specification !== "Plane"
        ? (parseFloat(product?.specificationMC || 0) *
            parseFloat(product?.specificationMakingJmi || 0)) /
            100 +
          (parseFloat(product?.specificationMC || 0) *
            parseFloat(product?.specificationMakingOperator || 0)) /
            100
        : 0);
    console.log(wastage);
    // 🔹 Common MC components
    const setMcBase = num(product?.setMc);
    const setMakingPercent =
      num(product?.setMakingJmi) + num(product?.setMakingOperator);
    const gramMcPerGram =
      num(product?.netGramMc) +
      num(product?.netGramMc) *
        ((num(product?.gramMakingJmi) + num(product?.gramMakingOperator)) /
          100);
    const specMcPerGram =
      num(product?.specificationGramRate) +
      num(product?.specificationGramRate) *
        ((num(product?.specificationGramRateJmi) +
          num(product?.specificationGramRateOperator)) /
          100);

    // 🔹 Totals
    const totals = {
      totalGrossWt: 0,
      totalNetWt: 0,
      totalFineWt: 0,
      totalMC: 0,
      totalSets: 0,
    };

    // 🔸 LOT Product: MC is calculated per variant with lot avgNetWt & avgSpecWt
    if (
      Array.isArray(product.lotDetails) &&
      Array.isArray(product.selectedVariants)
    ) {
      product.selectedVariants.forEach((variant) => {
        const lot = product.lotDetails.find(
          (l) => l && l.size === variant.size,
        );
        if (!lot) return;
        const qty = num(variant.quantity);
        const avgNet = num(lot.avgNetWt);
        const avgGross = num(lot.avgGrossWt);
        const avgSpec = num(lot.avgSpecWt);

        totals.totalSets += qty;
        totals.totalNetWt += avgNet * qty;
        totals.totalGrossWt += avgGross * qty;
        totals.totalFineWt += (avgNet * wastage) / 100;
        console.log(
          "size wise total fineWt",
          variant.size,
          "gross",
          variant.avgGrossWt,
          "net",
          avgNet,
          (qty * avgNet * wastage) / 100,
        );

        // ✅ LOT MC calculation
        const lotSetMC = setMcBase + (setMcBase * setMakingPercent) / 100;
        const lotGramMC = avgNet * gramMcPerGram;
        const lotSpecMC = avgSpec * specMcPerGram;

        totals.totalMC += (lotSetMC + lotGramMC + lotSpecMC) * qty;
      });
    }
    return totals;
  };
  const handleDeleteOrder = async (firebaseDocId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) {
      return;
    }
    try {
      setDeletingDocId(firebaseDocId);
      const order = orders.find((o) => o.firebaseDocId === firebaseDocId);
      if (!order) {
        throw new Error("Order not found");
      }
      const serviceType = String(order.serviceType || "").toLowerCase();
      const num = (value) => {
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) ? parsed : 0;
      };
      // =========================================================
      // ORDER SERVICE
      // =========================================================
      // If deleted order is "order" service:
      //
      // DELETE orderList ONLY
      // DO NOT TOUCH PRODUCT
      // =========================================================
      if (serviceType === "order") {
        await deleteDoc(doc(db, "orderList", firebaseDocId));
        setOrders((prev) =>
          prev.filter((item) => item.firebaseDocId !== firebaseDocId),
        );
        alert("Order deleted successfully!");
        return;
      }
      // =========================================================
      // READY SERVICE
      // =========================================================
      // If deleted order is "ready" service:
      //
      // 1. Get current product
      // 2. Restore ordered quantity size-wise
      // 3. Check all restored sizes
      // 4. ANY set > 0  => product serviceType = "ready"
      // 5. ALL set <= 0 => product serviceType = "order"
      // 6. Delete order + update product atomically
      // =========================================================
      if (serviceType === "ready") {
        if (!order.id) {
          throw new Error("Product ID is missing from order");
        }
        const productRef = doc(db, "products", order.id);
        const productSnap = await getDoc(productRef);
        if (!productSnap.exists()) {
          throw new Error("Product not found");
        }
        const productData = productSnap.data();
        // Current product lotDetails
        const currentLotDetails = Array.isArray(productData.lotDetails)
          ? productData.lotDetails
          : [];
        // Ordered variants
        const selectedVariants = Array.isArray(order.selectedVariants)
          ? order.selectedVariants
          : [];
        // =======================================================
        // BUILD SIZE -> QUANTITY TO RESTORE MAP
        // =======================================================
        const restoreQtyBySize = new Map();
        selectedVariants.forEach((variant) => {
          if (!variant || variant.size === undefined || variant.size === null) {
            return;
          }
          const size = String(variant.size);
          const quantity = num(variant.quantity);
          if (quantity <= 0) {
            return;
          }
          const existing = restoreQtyBySize.get(size) || 0;
          restoreQtyBySize.set(size, existing + quantity);
        });
        // =======================================================
        // RESTORE PRODUCT LOT QUANTITY
        // =======================================================
        const updatedLotDetails = currentLotDetails.map((lot) => {
          if (!lot || lot.size === undefined || lot.size === null) {
            return lot;
          }
          const size = String(lot.size);
          const restoreQty = restoreQtyBySize.get(size) || 0;
          if (restoreQty <= 0) {
            return lot;
          }
          const currentSet = num(lot.set);
          return {
            ...lot,
            set: currentSet + restoreQty,
          };
        });
        // =======================================================
        // CHECK PRODUCT STOCK AFTER RESTORATION
        // =======================================================
        //
        // ANY size with set > 0
        //     => READY
        //
        // ALL sizes with set <= 0
        //     => ORDER
        //
        // =======================================================
        const hasAvailableQuantity = updatedLotDetails.some(
          (lot) => num(lot?.set) > 0,
        );
        const updatedServiceType = hasAvailableQuantity ? "ready" : "order";
        console.log("Deleting READY order and restoring product:", {
          orderId: firebaseDocId,
          productId: order.id,
          restoreQtyBySize: Object.fromEntries(restoreQtyBySize),
          updatedLotDetails,
          updatedServiceType,
        });
        // =======================================================
        // ATOMIC FIRESTORE BATCH
        // =======================================================
        //
        // READY ORDER:
        //
        // DELETE orderList
        // +
        // RESTORE product quantity
        // +
        // UPDATE product serviceType
        //
        // =======================================================
        const batch = writeBatch(db);
        // Delete order
        batch.delete(doc(db, "orderList", firebaseDocId));
        // Update product
        batch.update(productRef, {
          lotDetails: updatedLotDetails,
          serviceType: updatedServiceType,
        });
        await batch.commit();
        setOrders((prev) =>
          prev.filter((item) => item.firebaseDocId !== firebaseDocId),
        );
        alert(
          hasAvailableQuantity
            ? "Order deleted and product quantity restored successfully!"
            : "Order deleted successfully!",
        );
        return;
      }
      // =========================================================
      // UNKNOWN SERVICE TYPE
      // =========================================================
      //
      // Safety fallback:
      // Delete order only.
      // Never modify product for unknown service types.
      // =========================================================
      console.warn("Unknown order serviceType:", order.serviceType);
      await deleteDoc(doc(db, "orderList", firebaseDocId));
      setOrders((prev) =>
        prev.filter((item) => item.firebaseDocId !== firebaseDocId),
      );
      alert("Order deleted successfully!");
    } catch (err) {
      console.error("Error deleting order:", err);
      if (err.code === "permission-denied") {
        alert("You don't have permission to delete this order.");
      } else {
        alert(err.message || "Failed to delete order.");
      }
    } finally {
      setDeletingDocId(null);
    }
  };

  // Get status badge text and color based on order status
  const getStatusInfo = (orderStatus) => {
    switch (orderStatus) {
      case "Requested":
        return { text: "Pending", color: "yellow" };
      case "Rejected":
        return { text: "Rejected", color: "red" };
      case "Assortment":
        return { text: "In Assortment", color: "blue" };
      case "Assorted":
        return { text: "In Assortment", color: "blue" };
      case "FinalCorrection":
        return { text: "Final Correction", color: "purple" };
      case "RTD":
        return { text: "Ready to Dispatch", color: "orange" };
      case "shipped":
        return { text: "Shipped", color: "teal" };
      case "out-for-delivery":
        return { text: "Out for Delivery", color: "green" };
      case "delivered":
        return { text: "Delivered", color: "green" };
      case "Cancelled":
        return { text: "Cancelled", color: "red" };
      default:
        return { text: orderStatus, color: "gray" };
    }
  };

  if (loading) {
    return (
      <div className="orders-container">
        <Header title={config.title} />
        <div className="loading-state">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-container">
        <Header title={config.title} />
        <div className="error-state">
          <p className="error">{error}</p>
          <button onClick={fetchOrders} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header title={config.title} />
      <div className="orders-container">
        {orders.length === 0 ? (
          <div className="empty-state">
            <p>No {config.title.toLowerCase()} found.</p>
            <button onClick={fetchOrders} className="retry-btn">
              Refresh List
            </button>
          </div>
        ) : (
          <div className="orders-grid">
            {orders.map((order) => {
              const imageUrl = order.images?.[0]?.url?.trim();
              const statusInfo = getStatusInfo(order.orderStatus);
              const isDeleting = deletingDocId === order.firebaseDocId;

              // Optional: calculate MC / weights for display
              const { totalFineWt, totalMC } = calculateProductWeights(order);

              return (
                <div key={order.firebaseDocId} className="ordercard">
                  {/* Product Image */}
                  <div className="orderimage">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={order.productName || "Product"}
                        loading="lazy"
                      />
                    ) : (
                      <div className="image-placeholder">No Image</div>
                    )}
                  </div>
                  <div className="select-product-info">
                    <span className="select-product-specification">
                      {order.category}/{order.productName}
                    </span>
                    <p className="select-product-name">
                      {order.specification}/{order.styleType}
                    </p>
                    <span style={{ fontSize: "0.8rem", fontWeight: "500" }}>
                      {order.serviceType.toUpperCase()}
                    </span>

                    {/* Display sizes and quantities */}
                    {order.selectedVariants?.length > 0 && (
                      <div className="variant-display">
                        {order.selectedVariants.map((variant, index) => (
                          <span key={variant.size} className="variant-info">
                            {variant.size} x {variant.quantity}
                            {index < order.selectedVariants.length - 1
                              ? ", "
                              : ""}
                          </span>
                        ))}
                      </div>
                    )}
                    
{(() => {
  const currentItem = order?.item || order?.product || order;

  const segment = String(
    currentItem?.segment || order?.segment || ""
  ).toUpperCase();

  const isCaratSegment = ["GEMS", "PEARL"].includes(segment);

  if (isCaratSegment) {
    const num = (value) => {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const maxCaratRateOperator = num(
      currentItem?.maxCaratRateOperator ??
      order?.maxCaratRateOperator
    );

    const maxCaratRateJmi = num(
      currentItem?.maxCaratRateJmi ??
      order?.maxCaratRateJmi
    );

    const firstLot = currentItem?.lotDetails?.[0];

    const baseCaratRate = num(
      firstLot?.caratRate ??
      currentItem?.caratRate ??
      currentItem?.maxCaratRate ??
      order?.caratRate
    );

    const calculatedCaratRate =
      baseCaratRate +
      (baseCaratRate * maxCaratRateOperator) / 100 +
      (baseCaratRate * maxCaratRateJmi) / 100;

    const caratRate = num(
      currentItem?.finalCaratRate ??
      order?.finalCaratRate
    ) || calculatedCaratRate;

    const variants =
      currentItem?.selectedVariants ||
      order?.selectedVariants ||
      [];

    let totalCaratWt = num(
      currentItem?.totalCaratWt ??
      order?.totalCaratWt ??
      currentItem?.caratWt ??
      order?.caratWt
    );

    if (totalCaratWt === 0 && variants.length > 0) {
      totalCaratWt = variants.reduce((sum, variant) => {
        const lot = (currentItem?.lotDetails || []).find(
          (item) => String(item.size) === String(variant.size)
        );

        const qty = num(variant.quantity) || 1;

        const weight = num(
          variant.caratWt ??
          lot?.caratWt ??
          variant.avgNetWt ??
          lot?.avgNetWt ??
          currentItem?.netWt ??
          order?.netWt
        );

        return sum + qty * weight;
      }, 0);
    }

    const savedAmount =
      currentItem?.amount ??
      order?.totalAmount ??
      order?.amount;

    const amount =
      savedAmount !== undefined &&
      savedAmount !== null &&
      savedAmount !== ""
        ? num(savedAmount)
        : totalCaratWt * caratRate;

    return (
      <>
        <p className="select-product-weight">
          Carat Wt: {totalCaratWt.toFixed(3)} ct
        </p>

        <p className="select-product-weight">
          Carat Rate: ₹{caratRate.toFixed(2)}/ct
        </p>

        <p className="select-product-weight">
          Amount: ₹{amount.toFixed(2)}
        </p>
      </>
    );
  }

  return (
    <>
      <p className="select-product-weight">
        Fine Wt: {(totalFineWt || 0).toFixed(3)} gm
      </p>

      <p className="select-product-weight">
        Amount: ₹{(totalMC || 0).toFixed(2)}
      </p>
    </>
  );
})()}


                    <div className={`status-badge status-${statusInfo.color}`}>
                      <span>{statusInfo.text}</span>
                    </div>
                    {config.status === "Requested" ? (
                      <button
                        className="selected-remove-btn"
                        onClick={() => handleDeleteOrder(order.firebaseDocId)}
                        disabled={isDeleting}
                      >
                        Remove
                      </button>
                    ) : (
                      ""
                    )}
                  </div>
                  {/* Product Info */}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default Orders;
